import { renderEmptyStateMarkup } from '../../emptyStateMarkup';

/**
 * Home learning sections markup.
 *
 *   1. Home hero - value prop, workbench preview, CTAs.
 *      AI-workbench stage placeholders are kept (the four stages are
 *      product copy: "Gathering source signals" / "Building the
 *      chapter path" / "Writing structured lessons" / "Final review");
 *      the *time* suffix on each stage is now a data-bind so the
 *      controller can render real elapsed seconds, or hide it
 *      entirely if no AI job is in flight.
 *      The "Try a Free Chapter" button was removed (dead
 *      data-bind="home.featuredSlug" placeholder).
 *
 *   2. Metrics - the member-metrics-strip (#memberMetricsSection)
 *      was removed as a dead UI field; its data-binds were never
 *      populated. See homeStateController.ts for the layout order.
 *
 *   3. Recently Opened + Saved topics - 4 hardcoded cards removed.
 *      Independent member sections are populated in parallel from
 *      /api/topics/recent and /api/user/favorites by
 *      homeRecentHydrator.
 *
 *   4. Each library list has its own header, View all action and
 *      compact empty host; there is no tab switch state.
 */
export const homeLearningSectionsMarkup = String.raw`<!-- Multimodal value strip -->
      <!-- Recently Opened / Saved topics -->
      <!-- Trending -->
      <!-- Community flywheel -->
    <section class="home-intelligence-hero free-guest-home" id="guestHeroSection">
        <div class="home-intelligence-copy">
          <div class="home-kicker">TOPICS · CHAPTER BY CHAPTER</div>
          <h1>Stop context-switching.<br>Start actually finishing.</h1>
          <p>Explore curated topics or turn sources you trust into a private topic. Follow clear, progressive chapters you can read, listen to, or watch—with progress synced across devices.</p>
          <div class="home-value-strip" aria-label="DeepMox advantages">
            <span class="home-value-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6h16M4 12h10M4 18h16"></path><path d="M17 10l2 2-2 2"></path></svg>
              Growing topic library
            </span>
            <span class="home-value-pill">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 6h16M4 12h10M4 18h16"></path><path d="M17 10l2 2-2 2"></path></svg>
              Clear learning path
            </span>
            <span class="home-value-pill home-value-pill--modes">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 5h6v14H4z"></path><path d="M14 5h6v14h-6z"></path><path d="M10 12h4"></path><path d="M7 9v6M17 8v8"></path></svg>
              Read · Listen · Watch
            </span>
          </div>
          <div class="home-hero-actions">
            <button class="btn btn-primary" data-proto-click="showLoginModal()">
              Start learning free
            </button>
            <button class="btn btn-outline" data-proto-click="navigateToBrowse('all')">Browse topics</button>
          </div>
          <div class="home-trust-note">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path><path d="M9 12l2 2 4-4"></path></svg>
            Start with public topics. Create a private topic when your own sources matter.
          </div>
        </div>

        <div class="ai-workbench" aria-label="AI Create generation preview">
          <div class="ai-workbench-top">
            <div class="ai-workbench-title">
              <svg class="course-structure-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                <path d="M7 5h9.5A2.5 2.5 0 0 1 19 7.5V19H8a3 3 0 0 1-3-3V7a2 2 0 0 1 2-2Z"></path>
                <path d="M8 19a3 3 0 0 1 3-3h8"></path>
                <path d="M9 9h6"></path>
                <path d="M9 12h4"></path>
                <path d="M15.5 11.5 18 14l-2.5 2.5"></path>
              </svg>
              Private topic preview
            </div>
            <div class="ai-workbench-status">Structuring</div>
          </div>
          <div class="ai-workbench-body">
            <div class="ai-query-box">
              <span class="ai-query-label">Topic request</span>
              <span>RAG systems in production: retrieval design, evals, observability, and failure modes</span>
            </div>
            <div class="ai-stage-list" role="list" aria-label="Topic generation progress">
              <div class="ai-stage-list-item" role="listitem" aria-label="Gathering sources — completed (05:22)"><div class="ai-stage ai-stage--completed"><span class="ai-stage-dot" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg></span><span>Gathering sources</span><span class="ai-stage-time">05:22</span></div><span class="ai-stage-connector" aria-hidden="true"></span></div>
              <div class="ai-stage-list-item" role="listitem" aria-label="Building the chapter path — completed (10:48)"><div class="ai-stage ai-stage--completed"><span class="ai-stage-dot" aria-hidden="true"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"></polyline></svg></span><span>Building the chapter path</span><span class="ai-stage-time">10:48</span></div><span class="ai-stage-connector" aria-hidden="true"></span></div>
              <div class="ai-stage-list-item" role="listitem" aria-current="step" aria-label="Writing structured chapters — in progress (71:31)"><div class="ai-stage ai-stage--active"><span class="ai-stage-dot" aria-hidden="true"></span><span>Writing structured chapters</span><span class="ai-stage-time">71:31</span></div><span class="ai-stage-connector" aria-hidden="true"></span></div>
              <div class="ai-stage-list-item" role="listitem" aria-label="Preparing read, listen, and watch modes — not started (next)"><div class="ai-stage ai-stage--pending"><span class="ai-stage-dot" aria-hidden="true"></span><span>Preparing read, listen, and watch modes</span><span class="ai-stage-time">next</span></div></div>
            </div>
            <div class="ai-outline">
              <div class="ai-outline-row"><span class="ai-outline-index">01</span><strong>Retrieval architecture</strong><span>11 min</span></div>
              <div class="ai-outline-row"><span class="ai-outline-index">02</span><strong>Chunking and hybrid search</strong><span>10 min</span></div>
              <div class="ai-outline-row"><span class="ai-outline-index">03</span><strong>Evaluation loops</strong><span>13 min</span></div>
              <div class="ai-outline-more"><span aria-hidden="true"></span><span>10 more</span><span aria-hidden="true"></span></div>
            </div>
          </div>
        </div>
      </section>

      <section class="home-mobile-library-section" id="mobileHomeLibrarySection" hidden aria-label="Your learning">
        <div class="home-mobile-section-header">
          <div class="home-mobile-text-tabs" role="tablist" aria-label="Your learning">
            <button type="button" role="tab" id="mobileRecentTab" aria-controls="mobileRecentPanel" aria-selected="true">Recent</button>
            <button type="button" role="tab" id="mobileSavedTab" aria-controls="mobileSavedPanel" aria-selected="false">Saved</button>
          </div>
          <a class="home-mobile-view-all" id="mobileRecentViewAll" href="/browse" data-proto-click="navigateToBrowse('recent');return false;">View all</a>
          <a class="home-mobile-view-all" id="mobileSavedViewAll" href="/browse" data-proto-click="navigateToBrowse('favorites');return false;" hidden>View all</a>
        </div>
        <div class="home-mobile-library-panel" id="mobileRecentPanel" role="tabpanel" aria-labelledby="mobileRecentTab"></div>
        <div class="home-mobile-library-panel" id="mobileSavedPanel" role="tabpanel" aria-labelledby="mobileSavedTab" hidden></div>
      </section>

      <section class="home-discovery-section home-discovery-recent" id="recentSection" data-visitor-soft="true" style="display:none;">
        <div class="section-header" id="recentHeaderSection">
          <div class="home-recent-header-skeleton" data-home-first-frame="recent" aria-hidden="true">
            <span class="home-member-first-frame__line home-member-first-frame__line--recent-title"></span>
            <span class="home-member-first-frame__line home-member-first-frame__line--recent-copy"></span>
          </div>
          <div>
            <h3 id="recentModuleTitle">Recently opened</h3>
          </div>
          <a href="/browse" data-proto-click="navigateToBrowse('recent');return false;">View all</a>
        </div>
        <div class="recent-content-wrap" id="recentContentSection">
          <div class="recent-list home-discovery-recent-list" id="recent-list" style="display:none;"></div>
          <div id="recentEmpty">${renderEmptyStateMarkup({ icon: 'clock', title: 'No recent activity yet', compact: true, hostClass: 'recent-empty' })}</div>
        </div>
      </section>

      <section class="home-discovery-section home-discovery-recent" id="savedSection" data-visitor-soft="true" style="display:none;">
        <div class="section-header" id="savedHeaderSection">
          <div class="home-recent-header-skeleton" data-home-first-frame="saved" aria-hidden="true">
            <span class="home-member-first-frame__line home-member-first-frame__line--recent-title"></span>
            <span class="home-member-first-frame__line home-member-first-frame__line--recent-copy"></span>
          </div>
          <div id="savedHeaderContent">
            <h3>Saved topics</h3>
          </div>
          <a id="savedHeaderViewAll" href="/browse" data-proto-click="navigateToBrowse('favorites');return false;">View all</a>
        </div>
        <div class="recent-content-wrap" id="favoritesContentSection">
          <div class="recent-list home-discovery-recent-list" id="favorites-list" style="display:none;"></div>
          <div id="favoritesEmpty">${renderEmptyStateMarkup({ icon: 'bookmark', title: 'No saved topics yet', compact: true, hostClass: 'recent-empty' })}</div>
        </div>
      </section>

      <!-- ====== Visitor CTA (replaces hidden recent content) ======
           Visible only for visitors via #view-home[data-auth="guest"]
           selector in hero.css. Closes the leaky dead zone where the
           "Recently Opened" header showed but the content stayed empty.
           Hooks a real signup action: data-proto-click="showRegisterModal()"
           opens the registration modal directly (vs. the older
           visitorGuard → loginModal path that required an extra click
           to switch tabs). -->
      <div class="visitor-recent-cta" id="visitorRecentCta" data-visitor-only="true">
        <div class="visitor-recent-cta-glow" aria-hidden="true"></div>
        <div class="visitor-recent-cta-grid">
          <div class="visitor-recent-cta-lead">
            <span class="visitor-recent-cta-badge">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="5"></circle><path d="M20 21a8 8 0 0 0-16 0"></path>
              </svg>
              Free account
            </span>
            <h4 class="visitor-recent-cta-title">Track your learning across devices</h4>
            <p class="visitor-recent-cta-desc">
              Save topics, keep your reading position, and pick up right where you left off — on any device, no setup.
            </p>
            <div class="visitor-recent-cta-actions">
              <button class="btn btn-primary" data-proto-click="showRegisterModal()">
                Create free account
              </button>
              <button class="btn btn-outline" data-proto-click="showLoginModal()" aria-label="Log in to your existing account">
                Already have one? <span class="visitor-recent-cta-login-link">Log in</span>
              </button>
            </div>
          </div>
          <ul class="visitor-recent-cta-benefits" aria-label="What you get with a free account">
            <li class="visitor-recent-cta-benefit">
              <span class="visitor-recent-cta-benefit-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                  <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path>
                </svg>
              </span>
              <span class="visitor-recent-cta-benefit-text">
                <strong>Bookmark topics</strong>
                <span class="visitor-recent-cta-benefit-detail">Save anything worth revisiting to your library.</span>
              </span>
            </li>
            <li class="visitor-recent-cta-benefit">
              <span class="visitor-recent-cta-benefit-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                  <circle cx="12" cy="12" r="9"></circle>
                  <polyline points="12 7 12 12 15.5 14"></polyline>
                </svg>
              </span>
              <span class="visitor-recent-cta-benefit-text">
                <strong>Resume anywhere</strong>
                <span class="visitor-recent-cta-benefit-detail">Your reading position follows you between sessions.</span>
              </span>
            </li>
            <li class="visitor-recent-cta-benefit">
              <span class="visitor-recent-cta-benefit-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7">
                  <rect x="4" y="3" width="10" height="16" rx="1.5"></rect>
                  <path d="M17 7h3v10a2 2 0 0 1-2 2h-1"></path>
                  <path d="M8 21h6"></path>
                </svg>
              </span>
              <span class="visitor-recent-cta-benefit-text">
                <strong>Every device</strong>
                <span class="visitor-recent-cta-benefit-detail">Phone, tablet, or laptop — one synced learning space.</span>
              </span>
            </li>
          </ul>
        </div>
      </div>`;
